// Toggles `past-hero` on <body> when the homepage hero banner has scrolled
// out of view. Used to reveal the floating pill nav after the hero.
class PastHero {
  constructor() {
    const banner = document.querySelector(".section__hero-banner");
    if (!banner) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle("past-hero", !entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(banner);
  }
}

// Scrollspy for the floating page-anchor nav. Marks the link whose target
// section is currently most prominent in the viewport (closest to a trigger
// line near the top) with `is-active`.
class PageAnchorScrollSpy {
  constructor() {
    const nav = document.querySelector(".page-anchors");
    if (!nav) return;

    this.pairs = [];
    nav.querySelectorAll(".page-anchors__link").forEach((link) => {
      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#") || href.length < 2) return;
      const target = document.querySelector(href);
      if (target) this.pairs.push({ link, target });
    });
    if (!this.pairs.length) return;

    this.bindClicks();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.update();
        ticking = false;
      });
    };

    this.update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
  }

  bindClicks() {
    this.pairs.forEach(({ link, target }) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        // Land the section's *content* (past its own top padding) at ~10%
        // of viewport from the top — gives breathing room without leaving
        // a big gap from the section's padding.
        const paddingTop =
          parseFloat(getComputedStyle(target).paddingTop) || 0;
        const offset = window.innerHeight * 0.1;
        const y =
          target.getBoundingClientRect().top +
          window.scrollY +
          paddingTop -
          offset;
        window.scrollTo({ top: y, behavior: "smooth" });
        history.replaceState(null, "", link.getAttribute("href"));
      });
    });
  }

  update() {
    // Active = last section whose top has crossed above the trigger line
    // (1/3 down from the top of the viewport).
    const trigger = window.innerHeight / 3;
    let activeIdx = -1;
    this.pairs.forEach(({ target }, idx) => {
      if (target.getBoundingClientRect().top <= trigger) activeIdx = idx;
    });

    // When the user has reached the bottom of the page, force the last link
    // active — short trailing sections may never reach the trigger line.
    const atBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;
    if (atBottom) activeIdx = this.pairs.length - 1;

    this.pairs.forEach(({ link }, idx) => {
      link.classList.toggle("is-active", idx === activeIdx);
      if (idx === activeIdx) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new PastHero();
  new PageAnchorScrollSpy();
});
